import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { useMountedState } from 'react-use';

import { useCreateSkipOnboardingWorkspace } from '~/components/Layouts/HomeLayout/hooks/useCreateSkipOnboardingWorkspace';
import { useHandleSkipOnboardingWorkspacesPoll } from '~/components/Layouts/HomeLayout/hooks/useHandleSkipOnboardingWorkspacesPoll';
import { CanvasQueryParams } from '~/constants/canvasQueryParams';
import { Routes } from '~/constants/routes';
import { QueryParamNames } from '~/constants/search';
import { useAccountContext } from '~/providers/AccountProvider';
import { useIsCurrentUserEmailVerified } from '~/swr-hooks/account/useIsCurrentUserEmailVerified';
import { useWorkspaces } from '~/swr-hooks/workspaces/useWorkspaces';
import { getSkipOnboardingEnabledFromQuery } from '~/utils/getSkipOnboardingEnabledFromQuery';
import { getWorkspaceNameFromQuery } from '~/utils/getWorkspaceNameFromQuery';

const getDefaultWorkspaceName = (firstName: string | undefined): string => {
  if (!firstName) {
    return 'My Workspace';
  }

  return `${firstName}'s workspace`;
};

export const useHandleSkipOnboarding = () => {
  const router = useRouter();
  const { data: account } = useAccountContext();
  const { data: workspaces, isFetched, refetch: refetchWorkspaces } = useWorkspaces();
  const { isFetched: isEmailVerificationFetched, userEmailIsVerified } = useIsCurrentUserEmailVerified();
  const hasHandledExistingWorkspacesRef = useRef<boolean>();
  const hasAttemptedCreateRef = useRef<boolean>();
  const isMounted = useMountedState();
  const [isLoading, setIsLoading] = useState(false);

  const shouldSkipOnboarding = getSkipOnboardingEnabledFromQuery(router.query);
  const workspaceName = getWorkspaceNameFromQuery(router.query) ?? getDefaultWorkspaceName(account?.firstName);
  const { createSkipOnboardingWorkspace } = useCreateSkipOnboardingWorkspace({
    setIsLoading,
    workspaceName,
  });

  useEffect(() => {
    if (!shouldSkipOnboarding) {
      hasHandledExistingWorkspacesRef.current = false;
      hasAttemptedCreateRef.current = false;
      setIsLoading(false);
    }
  }, [shouldSkipOnboarding]);

  useHandleSkipOnboardingWorkspacesPoll({
    hasWorkspaces: (workspaces?.length ?? 0) > 0,
    refetchWorkspaces,
    shouldSkipOnboarding,
  });

  useEffect(() => {
    if (!router.isReady || !shouldSkipOnboarding || !isFetched || !account || !isEmailVerificationFetched) {
      return;
    }

    if (userEmailIsVerified === false) {
      // useRedirectUnverifiedUser sends unverified users to /verify-user; do not
      // create a workspace here — navigation does not cancel an in-flight POST.
      return;
    }

    const workspaceCount = workspaces?.length ?? 0;
    const hasWorkspaces = workspaceCount > 0;

    if (hasWorkspaces) {
      if (hasHandledExistingWorkspacesRef.current) {
        return;
      }

      hasHandledExistingWorkspacesRef.current = true;
      setIsLoading(true);

      const redirectQuery = router.query[QueryParamNames.redirect];
      const explicitRedirect = typeof redirectQuery === 'string' && redirectQuery ? redirectQuery : null;
      const hasCanvasFlowParam =
        !!router.query[QueryParamNames.canvasName] || !!router.query[CanvasQueryParams.canvasId];

      if (!explicitRedirect && hasCanvasFlowParam) {
        const newQuery = { ...router.query };

        delete newQuery[QueryParamNames.skipOnboarding];

        void router
          .replace(
            {
              pathname: router.pathname,
              query: newQuery,
            },
            undefined,
            { shallow: false },
          )
          .finally(() => {
            if (isMounted()) {
              setIsLoading(false);
            }
          });

        return;
      }

      const redirect = explicitRedirect ?? Routes.home;

      void router.replace(redirect).finally(() => {
        if (isMounted()) {
          setIsLoading(false);
        }
      });

      return;
    }

    if (hasAttemptedCreateRef.current) {
      return;
    }

    hasAttemptedCreateRef.current = true;
    setIsLoading(true);

    void createSkipOnboardingWorkspace();
  }, [
    account,
    createSkipOnboardingWorkspace,
    isEmailVerificationFetched,
    isFetched,
    isMounted,
    router,
    shouldSkipOnboarding,
    userEmailIsVerified,
    workspaces?.length,
  ]);

  return {
    isLoading,
    shouldSkipOnboarding,
  };
};
